import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { authApi } from '../api/client'
import { useAuth } from '../context/AuthContext'

const schema = z.object({
  userId: z.string().min(1, '아이디를 입력해주세요'),
  password: z.string().min(1, '비밀번호를 입력해주세요'),
})
type FormData = z.infer<typeof schema>

export default function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [error, setError] = useState('')

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    setError('')
    try {
      const res = await authApi.login(data)
      login(res.token, { userId: res.userId, name: res.name, email: res.email, userLevel: res.userLevel ?? 'FREE_TRIAL' })
      navigate('/dashboard')
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? '로그인에 실패했습니다.')
    }
  }

  const handleNaverLogin = async () => {
    try {
      const url = await authApi.getNaverLoginUrl()
      window.location.href = url
    } catch {
      setError('네이버 로그인 연동에 실패했습니다.')
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* 좌측 패널 - 브랜딩 */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 flex-col justify-between p-12 relative overflow-hidden">
        {/* 배경 그라디언트 효과 */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />

        {/* 그리드 패턴 */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        {/* 로고 */}
        <div className="relative z-10">
          <img src="/gaia3d-logo.png" alt="Gaia3D" className="h-10 object-contain object-left" />
        </div>

        {/* 중앙 텍스트 */}
        <div className="relative z-10 space-y-6">
          <div className="space-y-2">
            <p className="text-blue-400 text-sm font-semibold tracking-widest uppercase">Mago 3D Reality Mesh Service</p>
            <h2 className="text-4xl font-bold text-white leading-tight">
              3D 공간 데이터를<br />현실로 연결합니다
            </h2>
          </div>
          <p className="text-slate-400 text-base leading-relaxed">
            메시 데이터를 3D 타일로 변환하고<br />
            웹 브라우저에서 바로 시각화하세요.
          </p>

          {/* 기능 목록 */}
          <ul className="space-y-3 pt-4">
            {['메시 데이터 자동 변환', '3D Tiles 실시간 뷰어', '변환 완료 이메일 알림'].map((item) => (
              <li key={item} className="flex items-center gap-3 text-slate-300 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* 하단 */}
        <div className="relative z-10 flex items-center gap-3">
          <p className="text-slate-500 text-xs">© 2024 Gaia3D, Inc. All rights reserved.</p>
          <span className="text-slate-700 text-xs">·</span>
          <Link to="/terms" className="text-slate-400 text-xs hover:text-slate-200 transition-colors">
            이용약관
          </Link>
        </div>
      </div>

      {/* 우측 패널 - 로그인 폼 */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center bg-white px-6 py-12">
        <div className="w-full max-w-sm">
          {/* 모바일 로고 */}
          <div className="lg:hidden mb-8 text-center">
            <img src="/gaia3d-logo.png" alt="Gaia3D" className="h-8 object-contain mx-auto" />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900">로그인</h1>
            <p className="mt-1 text-sm text-slate-500">Mago 3D Reality Mesh Service</p>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">아이디</label>
              <input
                {...register('userId')}
                className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="아이디를 입력하세요"
              />
              {errors.userId && <p className="mt-1 text-xs text-red-600">{errors.userId.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">비밀번호</label>
              <input
                {...register('password')}
                type="password"
                className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="비밀번호를 입력하세요"
              />
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {isSubmitting ? '로그인 중...' : '로그인'}
            </button>

            <div className="relative flex items-center gap-3">
              <div className="flex-1 border-t border-slate-200" />
              <span className="text-xs text-slate-400">또는</span>
              <div className="flex-1 border-t border-slate-200" />
            </div>

            <button
              type="button"
              onClick={handleNaverLogin}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-[#03C75A] hover:bg-[#02b350] text-white text-sm font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              <span className="font-extrabold text-base leading-none">N</span>
              네이버로 로그인
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            계정이 없으신가요?{' '}
            <Link to="/register" className="font-semibold text-blue-600 hover:text-blue-700">
              회원가입
            </Link>
          </p>

          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <Link to="/terms" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
              서비스 이용약관
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
